import { useCallback, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Bell,
  HelpCircle,
  Home as HomeIcon,
  Inbox,
  LayoutGrid,
  Search,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { useShortcut } from '../hooks/useShortcut';
import { useGoShortcuts } from '../hooks/useGoShortcuts';
import { useApi } from '../hooks/useApi';
import { CommandPalette } from './CommandPalette';
import { DeployUpdateBanner } from './DeployUpdateBanner';
import { HelpOverlay } from './HelpOverlay';
import { PreviewBanner } from './PreviewBanner';
import { ThemeMenu } from './ThemeMenu';
import { Toaster } from './Toaster';
import { TourEngine } from './TourEngine';
import { VariantSwitcher } from '../home/VariantSwitcher';
import { openCustomizeDrawer, useHomeVariant } from '../home/useHomeVariant';
import { cn } from '../lib/cn';
import type { MeResponse } from '../lib/types';

const NAV = [
  { to: '/', label: 'Home', icon: HomeIcon, end: true },
  { to: '/apps', label: 'All Apps', icon: LayoutGrid },
  { to: '/requests', label: 'My Requests', icon: Inbox, soon: true },
  { to: '/directory', label: 'Directory', icon: Users, soon: true },
];

const ADMIN_NAV: ReadonlyArray<{
  to: string;
  label: string;
  icon: typeof HomeIcon;
  end?: boolean;
  soon?: boolean;
  badgeKey?: 'pendingRequestCount';
}> = [{ to: '/admin', label: 'Admin', icon: ShieldCheck, badgeKey: 'pendingRequestCount' }];

const initialsOf = (name: string | undefined) =>
  (name ?? '??')
    .split(' ')
    .map((s) => s[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

export const Layout = () => {
  const { user } = useAuth();
  const me = useApi<MeResponse>('/api/me');
  const isAdmin = me.data?.role === 'ADMIN';
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const togglePalette = useCallback(() => setPaletteOpen((v) => !v), []);
  const openHelp = useCallback(() => setHelpOpen(true), []);
  useShortcut('k', { meta: true, ctrl: true }, togglePalette);
  useGoShortcuts(openHelp);

  // The variant switcher rides the header but only makes sense on Home.
  // We compute the variant here so the switcher can drive it; the Home page
  // listens to the same in-tab event channel via useHomeVariant.
  const routerLocation = useLocation();
  const onHome = routerLocation.pathname === '/' || routerLocation.pathname === '';
  const prefs = (me.data?.preferences as Parameters<typeof useHomeVariant>[0]) ?? null;
  const { variant, setVariant } = useHomeVariant(prefs);

  return (
    <div className="min-h-screen bg-bg-tertiary text-text-primary">
      <DeployUpdateBanner />
      <PreviewBanner />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <HelpOverlay open={helpOpen} onClose={() => setHelpOpen(false)} />
      <Toaster />
      <TourEngine />

      <header className="sticky top-0 z-30 border-b border-line bg-bg-primary">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-4">
          <Link
            to="/"
            aria-label="OneDesk home"
            className="flex items-center gap-3 transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange-light focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary rounded-md"
          >
            <img
              src="/brand/alphadirect-logo-orange.svg"
              alt="Alpha Direct Insurance"
              className="h-7 w-auto"
            />
            <div className="hidden h-6 w-px bg-line sm:block" aria-hidden="true" />
            <div className="hidden text-[11px] font-bold uppercase tracking-[0.22em] sm:block">
              <span className="text-brand-orange">One</span>
              <span className="text-text-primary">Desk</span>
            </div>
          </Link>
          <button
            type="button"
            onClick={togglePalette}
            className="ml-2 hidden h-8 w-72 items-center gap-2 rounded-md border border-line bg-bg-secondary px-3 text-sm text-text-tertiary transition-colors hover:border-line-strong md:flex"
            aria-label="Open command palette"
          >
            <Search className="h-4 w-4" aria-hidden="true" />
            <span className="flex-1 truncate text-left">Search apps, people, docs...</span>
            <kbd className="rounded border border-line px-1.5 py-0.5 font-mono text-[10px]">⌘K</kbd>
          </button>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={togglePalette}
              className="grid h-11 w-11 place-items-center rounded-md border border-line bg-bg-secondary text-text-secondary hover:text-text-primary md:hidden"
              aria-label="Search"
            >
              <Search className="h-4 w-4" />
            </button>
            <span className="hidden items-center gap-1.5 rounded-full bg-brand-navy-pale px-2.5 py-1 text-[11px] font-medium text-brand-navy md:inline-flex">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-navy" aria-hidden="true" />
              Signed in via Azure AD
            </span>
            {onHome && (
              <div className="hidden sm:block">
                <VariantSwitcher
                  variant={variant}
                  onChange={(next) => void setVariant(next)}
                  onCustomize={openCustomizeDrawer}
                />
              </div>
            )}
            <ThemeMenu />
            <button
              type="button"
              onClick={openHelp}
              className="grid h-11 w-11 place-items-center rounded-md border border-line bg-bg-secondary text-text-secondary hover:text-text-primary"
              aria-label="Help & tours"
              title="Help, shortcuts & guided tours (?)"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="grid h-11 w-11 place-items-center rounded-md border border-line bg-bg-secondary text-text-secondary hover:text-text-primary"
              aria-label="Notifications"
            >
              <Bell className="h-4 w-4" />
            </button>
            <Link
              to="/profile"
              className="grid h-11 w-11 place-items-center rounded-full bg-brand-orange text-sm font-semibold text-white transition-shadow hover:ring-2 hover:ring-brand-orange-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange-light"
              aria-label={`Profile · ${user?.displayName ?? 'User'}`}
              title={user?.displayName ?? ''}
            >
              {initialsOf(user?.displayName)}
            </Link>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl md:flex">
        <aside className="hidden w-48 shrink-0 border-r border-sidebar-border bg-sidebar text-sidebar-text md:block">
          <nav aria-label="Main navigation" className="sticky top-14 py-4">
            <SidebarSection items={NAV} />
            {isAdmin && (
              <>
                <div className="mt-4 px-4 text-[10px] font-semibold uppercase tracking-wider text-sidebar-muted">
                  Admin
                </div>
                <SidebarSection
                  items={ADMIN_NAV}
                  badges={{ pendingRequestCount: me.data?.pendingRequestCount ?? 0 }}
                />
              </>
            )}
          </nav>
        </aside>

        <main id="main" className="min-w-0 flex-1 px-4 py-6 pb-24 md:px-8 md:pb-8">
          <Outlet />
        </main>
      </div>

      <nav
        aria-label="Mobile navigation"
        className="fixed bottom-0 left-0 right-0 z-20 border-t border-line bg-bg-primary md:hidden"
      >
        <ul className="flex">
          {NAV.filter((n) => !n.soon).map((item) => (
            <li key={item.to} className="flex-1">
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'flex flex-col items-center gap-0.5 py-2.5 text-[10px]',
                    isActive ? 'text-accent' : 'text-text-secondary',
                  )
                }
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

const SidebarSection = ({
  items,
  badges,
}: {
  items: ReadonlyArray<{
    to: string;
    label: string;
    icon: typeof HomeIcon;
    end?: boolean;
    soon?: boolean;
    badgeKey?: 'pendingRequestCount';
  }>;
  badges?: { pendingRequestCount?: number };
}) => (
  <ul className="space-y-0.5">
    {items.map((item) => {
      const badgeCount = item.badgeKey && badges ? badges[item.badgeKey] ?? 0 : 0;
      const content = (
        <span className="flex items-center gap-2 px-4 py-2 text-sm">
          <item.icon className="h-4 w-4" />
          <span className="flex-1">{item.label}</span>
          {item.soon && (
            <span className="rounded bg-sidebar-hover px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-sidebar-muted">
              soon
            </span>
          )}
          {!item.soon && badgeCount > 0 && (
            <span
              className="grid min-w-[18px] place-items-center rounded-full bg-status-critical px-1.5 text-[10px] font-semibold text-white"
              aria-label={`${badgeCount} pending`}
            >
              {badgeCount > 99 ? '99+' : badgeCount}
            </span>
          )}
        </span>
      );
      return (
        <li key={item.to}>
          {item.soon ? (
            <span className="block cursor-not-allowed text-sidebar-muted opacity-60">
              {content}
            </span>
          ) : (
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'relative block transition-colors',
                  isActive
                    ? // Solid pill in the accent colour + brand contrast text +
                      // 3px left rail in the same accent so even when the active
                      // colour ≈ the surface, the rail still calls it out.
                      'bg-sidebar-active font-semibold text-accent-contrast shadow-sm before:absolute before:inset-y-1 before:left-0 before:w-[3px] before:rounded-r before:bg-sidebar-active'
                    : 'text-sidebar-text/85 hover:bg-sidebar-hover hover:text-sidebar-text',
                )
              }
            >
              {content}
            </NavLink>
          )}
        </li>
      );
    })}
  </ul>
);
